import { computed, inject, Injectable, signal } from '@angular/core';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import {
  ListingDataSource,
  ListingPageModel
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingTableComponent } from 'src/app/shared/components/listing-table/listing-table.component';
import { TAB_VALUE } from 'src/app/shared/components/listing-page/listing-page.types';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { OrderDirection } from '../devices/components/devices.models';
import {
  BASE_STATION_SEARCH_TYPES,
  BASE_STATION_DEFAULT_SEARCH_TYPE,
  BASE_STATION_SORT_FIELD
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import { BaseStationService } from './base-stations-listing.service';
import { BaseStationItemListResponseDto, BaseStationTable } from './base-stations-listing-page.types';
import { BaseStationListingFiltersSourceService } from './base-stations-listing-page-filters-source.service';
import { ConfiguredColumnComponent } from './components/configured-column.component';
import { ProvisioningStatusColumnComponent } from './components/provisioning-status-column.component';
import { DeviceIdColumnComponent } from './components/device-id-column.component';
import { ExportCsvConfig } from 'src/app/services/file/export-data.service';
import { TableData } from '@portal/shared/types/api-response.types';

@Injectable()
export class BaseStationListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly baseStationService = inject(BaseStationService);
  private readonly listingPageModelRegistry = inject(ListingPageModelRegistry);

  readonly pageId = ListingPageId.BaseStationsProvisioning;
  readonly pageTitle = TranslateRegistry.get.MAIN_MENU.TITLE.BASE_STATIONS;

  readonly searchTypeList = computed(() => BASE_STATION_SEARCH_TYPES);
  readonly selectedSearchType = computed(() => BASE_STATION_DEFAULT_SEARCH_TYPE);

  readonly sortField = BASE_STATION_SORT_FIELD;

  readonly sorting = computed(() => ({
    columnName: this.sortField.provisioningDateTime,
    orderDirection: OrderDirection.DESC
  }));

  readonly tableColumns = computed(() => [...this.baseColumns()]);

  readonly baseColumns = signal([
    {
      field: 'deviceId',
      title: TranslateRegistry.get.BASE_STATION_PROVISIONING.VIEW_TABLE.DEVICE_ID,
      width: 140,
      sortable: true,
      cmp: DeviceIdColumnComponent
    },
    {
      field: 'model',
      title: TranslateRegistry.get.BASE_STATION_PROVISIONING.VIEW_TABLE.MODEL,
      width: 150,
      sortable: true
    },
    {
      field: 'provisioningStatus',
      title: TranslateRegistry.get.BASE_STATION_PROVISIONING.VIEW_TABLE.PROVISIONING_STATUS,
      width: 200,
      sortable: true,
      cmp: ProvisioningStatusColumnComponent
    },
    {
      field: 'macId',
      title: TranslateRegistry.get.BASE_STATION_PROVISIONING.VIEW_TABLE.MAC_ID,
      width: 280,
      sortable: true
    },
    {
      field: 'vpnConfigured',
      title: TranslateRegistry.get.BASE_STATION_PROVISIONING.VIEW_TABLE.VPN_CONFIGURATION,
      width: 200,
      sortable: true,
      cmp: ConfiguredColumnComponent
    },
    {
      field: 'ranConfigured',
      title: TranslateRegistry.get.BASE_STATION_PROVISIONING.VIEW_TABLE.RAN_CONFIGURATION,
      width: 200,
      sortable: true,
      cmp: ConfiguredColumnComponent
    },
    {
      field: 'provisioningDateTime',
      title: TranslateRegistry.get.BASE_STATION_PROVISIONING.VIEW_TABLE.PROVISIONING_DATE_TIME,
      width: 220,
      sortable: true
    }
  ]);

  readonly dataSource: ListingDataSource<BaseStationTable> = {
    getAll: tableState => {
      return this.baseStationService.getTableData(tableState);
    },
    total: 0
  };

  readonly exportCsv = {
    fileName: 'Export-Base_Station_Provisioning',
    mapData: (data: TableData<BaseStationItemListResponseDto>): ExportCsvConfig[] => {
      return data.data.map(baseStation => ({
        deviceId: { title: 'Device ID', value: baseStation.deviceId },
        model: { title: 'Model', value: baseStation.model },
        provisioningStatus: { title: 'Provisioning Status', value: baseStation.provisioningStatus ?? '' },
        macId: { title: 'MAC ID', value: baseStation.macId ?? '' },
        vpnConfigured: { title: 'VPN Configuration', value: baseStation.vpnConfigured ? 'Yes' : 'No' },
        ranConfigured: { title: 'RAN Configuration', value: baseStation.ranConfigured ? 'Yes' : 'No' },
        provisioningDateTime: { title: 'Provisioning Date Time', value: baseStation.provisioningDateTime ?? '' }
      }));
    }
  };

  readonly tableCmp = ListingTableComponent;
  readonly defaultTab = TAB_VALUE.Table;
  readonly exportButtonVisible = true;

  readonly filterSource = inject(BaseStationListingFiltersSourceService);

  constructor() {
    super();
    this.listingPageModelRegistry.add(this.pageId, this);
  }
}
